import moment, { Moment } from 'moment-timezone'
import { TimeRange, RelativeTimeRange, NullableMomentRange, RELATIVE_RANGE_LABELS, PartialTimeRange, NullablePartialMomentRange } from './types'
import { Range } from 'react-date-range'
import { TimeRangeBuilder } from '../../lib/time'

export function convertToAbsoluteTimeRange(timeRange: TimeRange, timezone: string): NullableMomentRange;
export function convertToAbsoluteTimeRange(timeRange: PartialTimeRange, timezone: string): NullablePartialMomentRange;

export function convertToAbsoluteTimeRange(timeRange: PartialTimeRange, timezone: string): NullablePartialMomentRange {
  const builder = new TimeRangeBuilder({ timezone })
  if (timeRange.timeRange) return timeRange.timeRange

  switch (timeRange.label) {
    case 'Today': return builder.today()
    case 'Yesterday': return builder.yesterday()
    case 'This Week': return builder.weekToDate()
    case 'Last Week': return builder.lastWeek()
    case 'This Month': return builder.monthToDate()
    case 'Last Month': return builder.lastMonth()
    case 'This Quarter': return builder.quarterToDate()
    case 'Last Quarter': return builder.lastQuarter()
    case 'This Year': return builder.yearToDate()
    case 'Last Year': return builder.lastYear()
    case 'Lifetime': return null
    default: return null
  }
}

// Given a (possibly-timezoned) Moment instance, create a Date that equals
// midnight in the browser's local time on the year/month/day of that Date
// instance.
export function momentToLocalDate(moment: Moment): Date {
  return new Date(moment.year(), moment.month(), moment.date())
}

export function valueToDateRange(value: NullablePartialMomentRange): Range {
  if (value && value[0]) {
    return {
      startDate: momentToLocalDate(value[0]),
      endDate: value[1] ? momentToLocalDate(value[1]) : momentToLocalDate(value[0]),
      key: 'selection'
    }
  }
  return { key: 'selection' }
}

export function dateRangeToTimeRange(range: Range, currentValue: NullablePartialMomentRange, timezone: string): TimeRange {
  const setYMD = (m: Moment, d: Date) => m.clone().set({ year: d.getFullYear(), month: d.getMonth(), date: d.getDate() })
  if (range.startDate && range.endDate) {
    const startMoment = currentValue?.[0] ? setYMD(currentValue[0], range.startDate) : moment.tz([range.startDate.getFullYear(), range.startDate.getMonth(), range.startDate.getDate()], timezone).startOf('day')
    const endMoment   = currentValue?.[1] ? setYMD(currentValue[1], range.endDate)   : moment.tz([range.endDate.getFullYear(), range.endDate.getMonth(), range.endDate.getDate()], timezone).endOf('day')

    return {
      timeRange: [startMoment, endMoment],
    }
  }
  return { label: 'Lifetime' }
}

export function generatePredefinedTimeRanges(): RelativeTimeRange[] {
  return RELATIVE_RANGE_LABELS.map(label => ({ label }))
}

export function isSameTimeRange(a: PartialTimeRange, b: PartialTimeRange): boolean {
  if (a.timeRange && b.timeRange) {
    return a.timeRange[0] !== null && a.timeRange[1] !== null && a.timeRange[0].isSame(b.timeRange[0]) && a.timeRange[1].isSame(b.timeRange[1])
  }
  return a.label === b.label
}

export function isValidTimeRange(value: PartialTimeRange): value is TimeRange {
  return !!(value.label || (value.timeRange?.[0]?.isValid() && value.timeRange?.[1]?.isValid()))
}

export function isStartOfDayEndOfDay(timeRange : NullablePartialMomentRange): boolean {
  if(timeRange === null) {
    return false
  }
  const [start, end] = timeRange
  if(start === null || end === null) {
    return false
  }
  return start.hour() === 0 && start.minute() === 0 && end.hour() === 23 && end.minute() === 59
}
